import type { TrackEventRequest } from "@b2bportal/event-tracking-api";
import type { AxiosInstance } from "axios";

const analyticsApiPrefix = "/api/v0/tracking";

/**
 * Late-bound reference to the shared axios instance.
 * Set via `setTrackingAxiosInstance` during app bootstrap so
 * core-tracking never imports directly from `@hopper-b2b/api`,
 * avoiding a circular NX dependency.
 */
let _axiosInstance: AxiosInstance | null = null;

export function setTrackingAxiosInstance(instance: AxiosInstance): void {
  _axiosInstance = instance;
}

function getAxiosInstance(): AxiosInstance {
  if (!_axiosInstance) {
    throw new Error(
      "TrackingBatcher: axiosInstance not set. Call setTrackingAxiosInstance() during app bootstrap."
    );
  }
  return _axiosInstance;
}

const ABSOLUTE_URL = /^(?:[a-z][a-z\d+\-.]*:)?\/\//i;

/**
 * Apply the axios instance's `baseURL` by hand.
 *
 * The exit flush uses `fetch` instead of axios, so it does not inherit
 * `baseURL`. Tenants served under a path prefix (Virgin AU sets it to
 * `/au-en`) only route the prefixed path to their BFF, so an unprefixed
 * endpoint never reaches the tracking service.
 */
function resolveEndpoint(endpoint: string): string {
  if (ABSOLUTE_URL.test(endpoint)) {
    return endpoint;
  }

  // Read the instance directly: the exit flush must never throw.
  const baseURL = _axiosInstance?.defaults?.baseURL;
  if (!baseURL) {
    return endpoint;
  }

  return `${baseURL.replace(/\/+$/, "")}/${endpoint.replace(/^\/+/, "")}`;
}

/**
 * Everything AxiosInterceptor would have added to a tracking request, for the
 * exit flush to apply by hand. Resolved at flush time rather than snapshotted,
 * so values that change after mount (the device id is written from a response
 * header) are not stale.
 *
 * `properties` is the event's complete property set, not an overlay: the
 * enricher is handed the queued event's own properties and folds them in the
 * same way the interceptor does. Some of that folding appends rather than
 * replaces (`experiments`, `local_experiments`), so merging an overlay on top
 * would erase arrays the event brought with it.
 */
export type ExitFlushEnrichment = {
  headers?: Record<string, string>;
  properties?: Record<string, unknown>;
};

export type ExitFlushEnricher = (
  eventProperties?: Record<string, unknown>
) => ExitFlushEnrichment;

interface QueuedEvent {
  request: TrackEventRequest;
  endpoint: string;
  resolve: (value: boolean) => void;
  retries: number;
  onStatusChange?: (status: string) => void;
}

const FLUSH_INTERVAL_MS = 3_000;
const MAX_QUEUE_SIZE = 20;
const MAX_CONCURRENT = 3;
const MAX_RETRIES = 2;
/**
 * Applied per request rather than on the instance, which is shared with callers
 * that legitimately poll for minutes. Without it a request that never settles
 * (a suspended WebView drops in-flight XHRs silently) holds a `#inFlight` slot
 * forever, and once all of them are held every later event waits indefinitely.
 */
const REQUEST_TIMEOUT_MS = 10_000;

class TrackingBatcher {
  #queue: QueuedEvent[] = [];
  #timerId: ReturnType<typeof setTimeout> | null = null;
  #inFlight = 0;
  #started = false;
  #handlersRegistered = false;
  #waiters: (() => void)[] = [];
  #exitFlushEnricher: ExitFlushEnricher | null = null;

  /**
   * Register the source of headers and properties for exit-flush fetch() calls.
   * Called by AxiosInterceptor so exit-flushed events carry the same headers
   * and tenant/locale/device/experiment properties as batched ones.
   */
  public setExitFlushEnricher(enricher: ExitFlushEnricher | null): void {
    this.#exitFlushEnricher = enricher;
  }

  #getExitFlushEnrichment(
    eventProperties?: Record<string, unknown>
  ): ExitFlushEnrichment {
    try {
      return this.#exitFlushEnricher?.(eventProperties) ?? {};
    } catch {
      // Missing enrichment is better than a dropped event
      return {};
    }
  }

  /**
   * Add an event to the batch queue.
   * Returns a promise that resolves when the event is actually sent.
   */
  enqueue(
    request: TrackEventRequest,
    endpoint?: string,
    onStatusChange?: (status: string) => void
  ): Promise<boolean> {
    const ep = endpoint ?? `${analyticsApiPrefix}/event`;

    return new Promise<boolean>((resolve) => {
      this.#queue.push({
        request,
        endpoint: ep,
        resolve,
        retries: 0,
        onStatusChange,
      });

      if (this.#queue.length >= MAX_QUEUE_SIZE) {
        this.flush();
      } else if (!this.#timerId) {
        this.#timerId = setTimeout(() => this.flush(), FLUSH_INTERVAL_MS);
      }

      if (!this.#started) {
        this.#started = true;
        this.#registerExitHandlers();
      }
    });
  }

  /**
   * Flush all queued events, respecting the concurrency limit.
   */
  flush(): void {
    if (this.#timerId) {
      clearTimeout(this.#timerId);
      this.#timerId = null;
    }

    const batch = this.#queue.splice(0);
    if (batch.length === 0) return;

    for (const item of batch) {
      this.#sendWithConcurrencyLimit(item);
    }
  }

  async #sendWithConcurrencyLimit(item: QueuedEvent): Promise<void> {
    if (this.#inFlight >= MAX_CONCURRENT) {
      await new Promise<void>((resolve) => this.#waiters.push(resolve));
    }

    this.#inFlight++;
    try {
      item.onStatusChange?.("sending");
      const res = await getAxiosInstance().post(item.endpoint, item.request, {
        timeout: REQUEST_TIMEOUT_MS,
      });
      item.onStatusChange?.("sent");
      item.resolve(res.data);
    } catch {
      if (item.retries < MAX_RETRIES) {
        this.#log("warn", `Tracking event retry`, {
          endpoint: item.endpoint,
          attempt: item.retries + 1,
          maxRetries: MAX_RETRIES,
        });
        item.onStatusChange?.("retrying");
        this.#queue.push({ ...item, retries: item.retries + 1 });
        if (!this.#timerId) {
          const backoff = FLUSH_INTERVAL_MS * Math.pow(2, item.retries + 1);
          this.#timerId = setTimeout(() => this.flush(), backoff);
        }
      } else {
        this.#log("error", `Tracking event failed permanently`, {
          endpoint: item.endpoint,
          attempts: MAX_RETRIES + 1,
        });
        item.onStatusChange?.("failed");
        item.resolve(false);
      }
    } finally {
      this.#inFlight--;
      if (this.#waiters.length > 0) {
        const next = this.#waiters.shift()!;
        next();
      }
    }
  }

  #log(
    level: "warn" | "error",
    message: string,
    context?: Record<string, unknown>
  ): void {
    try {
      const ddLogger =
        typeof window !== "undefined" && (window as any).DD_LOGS?.logger;
      if (ddLogger) ddLogger[level](message, context);
    } catch {
      // Logging must never throw
    }
  }

  /**
   * Best-effort exit flush using `fetch` with `keepalive`.
   *
   * **Important**: This bypasses AxiosInterceptor, so anything it normally adds
   * has to be applied by hand. Headers (device id, Accept-Language, currency)
   * and enriched properties (experiments, device metadata, UTM, tenant, locale,
   * etc.) come from the enricher AxiosInterceptor registers via
   * `setExitFlushEnricher()`, and `resolveEndpoint()` applies the axios
   * `baseURL` that axios would otherwise add itself.
   *
   * Uses `mode: "same-origin"` to avoid CORS preflight, which would block
   * keepalive requests during page teardown.
   */
  #flushOnExit(): void {
    const batch = this.#queue.splice(0);
    if (batch.length === 0) return;

    if (this.#timerId) {
      clearTimeout(this.#timerId);
      this.#timerId = null;
    }

    for (const item of batch) {
      const { headers: exitHeaders, properties: exitProperties } =
        this.#getExitFlushEnrichment(item.request.properties);

      try {
        // fetch with keepalive survives page unload; see method JSDoc for caveats
        fetch(resolveEndpoint(item.endpoint), {
          method: "POST",
          // same-origin skips CORS preflight; preflight + keepalive fails during page hide
          mode: "same-origin",
          headers: {
            "Content-Type": "application/json",
            ...exitHeaders,
          },
          body: JSON.stringify({
            ...item.request,
            // The enricher returns the merged set; fall back to the event's own
            // properties when no enricher is registered
            properties: exitProperties ?? item.request.properties,
          }),
          keepalive: true,
          credentials: "include",
          priority: "low",
        }).catch(() => {});
      } catch {
        // Best-effort: swallow errors during page exit
      }
      item.onStatusChange?.("exit-flush");
      item.resolve(false); // Resolve immediately — caller won't be around to check
    }
  }

  #registerExitHandlers(): void {
    if (typeof document === "undefined") return;
    if (this.#handlersRegistered) return;
    this.#handlersRegistered = true;

    // visibilitychange:hidden is the reliable cross-platform exit signal
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        this.#flushOnExit();
      }
    });

    // pagehide as backup (fires on navigation, tab close)
    window.addEventListener("pagehide", () => {
      this.#flushOnExit();
    });

    // Restore batcher state when page is restored from bfcache
    window.addEventListener("pageshow", (e) => {
      if ((e as PageTransitionEvent).persisted) {
        this.#started = true;
        if (this.#queue.length > 0 && !this.#timerId) {
          this.#timerId = setTimeout(() => this.flush(), FLUSH_INTERVAL_MS);
        }
      }
    });
  }

  /** Visible for testing */
  get queueLength(): number {
    return this.#queue.length;
  }

  /** Visible for testing */
  get activeRequests(): number {
    return this.#inFlight;
  }

  /** Reset state — for testing only */
  reset(): void {
    this.#queue = [];
    if (this.#timerId) {
      clearTimeout(this.#timerId);
      this.#timerId = null;
    }
    this.#inFlight = 0;
    this.#started = false;
    this.#handlersRegistered = false;
    this.#waiters = [];
    this.#exitFlushEnricher = null;
  }
}

export const trackingBatcher = new TrackingBatcher();
export { TrackingBatcher };

export function setExitFlushEnricher(enricher: ExitFlushEnricher | null): void {
  trackingBatcher.setExitFlushEnricher(enricher);
}

export function resetTrackingBatcher(): void {
  trackingBatcher.reset();
}
